import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useUser } from "context/UserContext";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";
import * as Yup from 'yup';

const ActionCreateEdit = ({ id, closeCreateEdit }) => {

    const { nome, justica } = configForm.formField

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);
    // Equipes da cobrança jurídica que usam a ação (só elas veem a ação na ficha; o Jurídico vê todas)
    const [equipesCobranca, setEquipesCobranca] = useState([]);
    useEffect(() => {
        get("/acao/equipes-cobranca").then(res => setEquipesCobranca(Array.isArray(res) ? res : [])).catch(() => setEquipesCobranca([]));
    }, []);

    const {
        tipoJusticaEnum,
    } = useUser()

    const handleSubmit = (values, actions) => {
        const body = {
            id: values.id,
            nome: values.nome,
            justica: values.justica?.code
        };

        // Grava a ação e, com o id que volta (também na nova), as equipes da cobrança
        post("/acao/save", body)
            .then(res => post(`/acao/${res?.id || values.id}/equipes`, { equipeIds: (values.equipes || []).map(e => e.id) }))
            .then(() => {
                successAlert(!id ? "Ação criada com sucesso!" : "Alterações salvas!", handleSucess());
            })
            .catch(() => {
                errorAlert(!id ? "Erro ao criar ação!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    function handleSucess() {
        closeCreateEdit()
    }

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Ação</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    nome: '',
                    justica: null,
                    equipes: []
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                id={id}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit}
                                tipoJusticaEnum={tipoJusticaEnum}
                                equipesCobranca={equipesCobranca} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={3}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type={nome.type}
                                            label={nome.label}
                                            name={nome.name}
                                            value={values.nome}
                                            placeholder={nome.placeholder}
                                            error={errors.nome?.length > 0 && touched.nome}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Justiça</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            isMulti={false}
                                            getOptionValue={option => option.code}
                                            getOptionLabel={option => option.name}
                                            name={justica.name}
                                            placeholder={justica.placeholder}
                                            value={values.justica}
                                            options={tipoJusticaEnum}
                                            onChange={selectedOptions => {
                                                setFieldValue('justica', selectedOptions);
                                            }}
                                        />
                                        {errors.justica && touched.justica && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.justica}
                                            </MDTypography>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={5}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Equipes da cobrança jurídica que usam</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            isMulti
                                            getOptionValue={option => option.id}
                                            getOptionLabel={option => option.nome}
                                            name="equipes"
                                            placeholder="Nenhuma: não aparece na ficha"
                                            value={values.equipes}
                                            options={equipesCobranca}
                                            onChange={selecionadas => setFieldValue('equipes', selecionadas || [])}
                                        />
                                        <MDTypography variant="caption" color="text" display="block" px={1}>
                                            Na ficha (Tipo de Ação do processo e Débito da migração para o Jurídico) só aparecem as ações
                                            habilitadas para as equipes da carteira jurídica. O Jurídico (processos) usa todas.
                                        </MDTypography>
                                    </Grid>
                                </Grid>
                            </MDBox>
                            <Box display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </Box>
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

ActionCreateEdit.propTypes = {
    id: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(ActionCreateEdit);

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit, tipoJusticaEnum, equipesCobranca }) => {
    const { setFieldValue } = useFormikContext();
    const [equipeIds, setEquipeIds] = useState(null);

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/acao/by-id/${id}`).then(data => {
                const { id, nome, justica } = data;
                setFieldValue('justica', tipoJusticaEnum.find(x => x.code == justica));
                setFieldValue('nome', nome);
                setFieldValue('id', id);
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Ação não encontrada!", closeCreateEdit));
            get(`/acao/${id}/equipes`).then(res => setEquipeIds(res?.equipeIds || [])).catch(() => setEquipeIds([]));
        }
    }, [id]);

    // As equipes gravadas, quando a lista de opções chegar
    useEffect(() => {
        if (equipeIds === null) return;
        setFieldValue('equipes', (equipesCobranca || []).filter(e => equipeIds.includes(e.id)));
    }, [equipeIds, equipesCobranca]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        nome: {
            name: "nome",
            label: "Nome",
            placeholder: "Informe o nome...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        justica: {
            name: "justica",
            label: "Justiça",
            placeholder: "Selecione a justiça",
            errorMsg: "Campo Obrigatório.",
        },
    }
}

const {
    formField: {
        nome,
        justica
    },
} = configForm;

export const validationSchema = Yup.object().shape({
    [nome.name]: Yup.string()
        .required(nome.errorMsg),
    [justica.name]: Yup.object()
        .required(justica.errorMsg),
});
